import {
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
} from "@angular/core";
import { DecimalPipe } from "@angular/common";
import { HelperExpand } from "../../../../shared/utils/helper-expand";
import { MacroNumberPipe } from "@bitwarden/web-vault/app/pipes/macro-number.pipe";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./path-tabs.component.scss";

/**
 * A path's final balance in whole dollars, sign first like the HUD's: -$2,004,821,788.
 * formatDigits formats the absolute value (default: grouped), e.g. the compact -$2.00B.
 */
export function formatPathAmount(
  amount: number,
  formatDigits: (absolute: number) => string = (absolute) => absolute.toLocaleString("en-US"),
): string {
  const rounded = Math.round(amount);
  return `${rounded < 0 ? "-" : ""}$${formatDigits(Math.abs(rounded))}`;
}

/** What a path tab shows for one amount; see the template. */
export interface PathAmountBadge {
  full: string;
  /** -$2.00B: the app's macroNumber form, shown by app-large-amount only when the full one
   * doesn't fit the tab. */
  compact: string;
  /** Hover text, and what screen readers get: always the full amount. */
  label: string;
}

@Component({
  selector: "app-path-tabs",
  templateUrl: "./path-tabs.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  // MacroNumberPipe isn't providedIn root; it's used from code here (see amountBadge).
  providers: [MacroNumberPipe, DecimalPipe],
})
export class PathTabsComponent implements OnInit, OnChanges {
  @Input() options: { label: string; value: string; index?: number }[] = [];
  @Input() finalBalanceAmountList: number[] = [];
  @Input() defaultTabSelected: string;
  @Output() onChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() isExpanded: EventEmitter<boolean> = new EventEmitter<boolean>();
  selectedTab: string;

  helperExpand: HelperExpand;
  private userStoreService = inject(UserStoreService);
  private macroNumber = inject(MacroNumberPipe);
  /** Per tab position: whether its full amount fits the tab, as its app-large-amount measured. */
  private readonly fullFitsByTab = signal<boolean[]>([]);

  get currency() {
    return this.userStoreService.preferences.preferenceView()?.coreCurrency?.toUpperCase() || "";
  }

  /** The tab's final balance in each form it may show, or null when there's none yet. */
  amountBadge(index: number | undefined): PathAmountBadge | null {
    const amount = index === undefined ? undefined : this.finalBalanceAmountList?.[index];
    // A balance of exactly 0 is still a balance, so only a missing one hides the badge.
    if (amount === null || amount === undefined || Number.isNaN(amount)) {
      return null;
    }
    const full = formatPathAmount(amount);
    return {
      full,
      compact: formatPathAmount(amount, (absolute) => this.macroNumber.transform(absolute)),
      label: this.currency ? `${full} ${this.currency}` : full,
    };
  }

  /**
   * Whether every tab shows its compact amount (#6169): true once any tab showing an amount can't
   * fit its full one, so the tabs never mix "$2.00B" and "$2,109,117,252".
   */
  compactAll(): boolean {
    const fits = this.fullFitsByTab();
    return this.options.some(
      (option, i) => fits[i] === false && this.amountBadge(option.index) !== null,
    );
  }

  setFullFits(tab: number, fits: boolean): void {
    this.fullFitsByTab.update((all) => {
      const next = [...all];
      next[tab] = fits;
      return next;
    });
  }

  ngOnInit() {
    this.helperExpand = new HelperExpand();
    const defaultValue = this.defaultTabSelected || this.options[0]?.value;
    this.setSelectedTab(defaultValue);
    this.isExpanded.emit(this.helperExpand.isExpanded);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["defaultTabSelected"] && changes["defaultTabSelected"].currentValue !== undefined) {
      this.setSelectedTab(changes["defaultTabSelected"].currentValue);
    }
  }

  onTabChange(value: string) {
    // If clicking the already selected tab, toggle the helper expansion
    if (this.selectedTab === value) {
      this.helperExpand.toggleContent(null);
      this.isExpanded.emit(this.helperExpand.isExpanded);
      return;
    }

    // Otherwise, switch to the new tab and collapse helper
    this.setSelectedTab(value);
    this.onChange.emit(value);
    this.helperExpand.setExpanded(false);
    this.isExpanded.emit(this.helperExpand.isExpanded);
  }

  setSelectedTab(value: string) {
    this.selectedTab = value;
  }

  isSelectedTab(value: string) {
    return this.selectedTab === value;
  }

  getSelectedTabIndex(): number {
    const index = this.options.findIndex((option) => option.value === this.selectedTab);
    return index >= 0 ? index : 0;
  }

  handleExpand(event: any) {
    this.helperExpand.toggleContent(event);
    this.isExpanded.emit(this.helperExpand.isExpanded);
  }
}
